import { useFeatureFlags } from "@cx-component-library/react-components/feature-flags";
import type { AppConfigFeatureFlags } from "feature-flags";
import type { UserAttributes } from "hooks/authentication";
import type { Dataset } from "models/colors";
import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useMemo,
} from "react";
import { resolveDataset } from "service/utils/dataset";
import { checkInternalUser } from "service/utils/get-user-type";
import { SettingsContext } from "./SettingsProvider";

export interface DatasetContextValue {
  /** Dataset the search, filter and autocomplete requests must ask for. */
  dataset: Dataset;
  /** `datasetSwitch` feature flag; the settings page also needs an internal user. */
  isSwitchEnabled: boolean;
}

export const DatasetContext = createContext<DatasetContextValue>({
  dataset: "original",
  isSwitchEnabled: false,
});

export const useDataset = (): DatasetContextValue => useContext(DatasetContext);

interface DatasetProviderProps {
  user: UserAttributes | undefined;
  children: ReactNode;
}

/**
 * Resolves the effective dataset once, so every data hook reads the same
 * gated value instead of re-deriving the user type and the flag per request.
 * Must sit inside `SettingsProvider`.
 */
export const DatasetProvider: FC<DatasetProviderProps> = ({
  user,
  children,
}) => {
  const { settings } = useContext(SettingsContext);
  const [flags] = useFeatureFlags<AppConfigFeatureFlags>();
  const isSwitchEnabled = flags.datasetSwitch?.enabled === true;
  const isInternal = checkInternalUser(user, settings);

  const value = useMemo(
    () => ({
      dataset: resolveDataset({
        dataset: settings.dataset,
        isInternal,
        flagEnabled: isSwitchEnabled,
      }),
      isSwitchEnabled,
    }),
    [settings.dataset, isInternal, isSwitchEnabled],
  );

  return (
    <DatasetContext.Provider value={value}>{children}</DatasetContext.Provider>
  );
};
